import { useQuery, useMutation } from '@tanstack/react-query'

import { get, getPaginated } from '@/lib/api'

// ==================== Types ====================

export type AuditAction = 'create' | 'update' | 'delete' | 'approve' | 'reject' | 'login' | 'logout'

export interface AuditLog {
  id: string
  employeeId: string
  employeeName: string
  action: AuditAction
  module: string
  entityType: string
  entityId: string | null
  summary: string
  details: Record<string, unknown> | null
  ipAddress: string | null
  userAgent: string | null
  requestId: string | null
  createdAt: string
  targetType?: string | null
  targetId?: string | null
  oldValue?: unknown
  newValue?: unknown
}

export interface AuditLogFilters {
  employeeId?: string
  action?: AuditAction
  module?: string
  startDate?: string
  endDate?: string
  page?: number
  pageSize?: number
}

export interface AuditLogExportParams {
  employeeId?: string
  action?: AuditAction
  module?: string
  startDate?: string
  endDate?: string
}

// ==================== Query Keys ====================

export const auditLogKeys = {
  all: ['audit-logs'] as const,
  lists: () => [...auditLogKeys.all, 'list'] as const,
  list: (filters: AuditLogFilters) => [...auditLogKeys.lists(), filters] as const,
  detail: (id: string) => [...auditLogKeys.all, 'detail', id] as const,
}

// ==================== Hooks ====================

/**
 * 获取审计日志列表（分页）
 */
export function useAuditLogs(filters: AuditLogFilters = {}) {
  return useQuery({
    queryKey: auditLogKeys.list(filters),
    queryFn: () => getPaginated<AuditLog>('/system/audit-logs', filters as Record<string, unknown>),
  })
}

/**
 * 获取审计日志详情
 */
export function useAuditLogDetail(id: string) {
  return useQuery({
    queryKey: auditLogKeys.detail(id),
    queryFn: () => get<AuditLog>(`/system/audit-logs/${id}`),
    enabled: !!id,
  })
}

/**
 * 导出审计日志为 CSV
 */
export function useExportAuditLogs() {
  return useMutation({
    mutationFn: async (params: AuditLogExportParams) => {
      const queryParams = new URLSearchParams()
      if (params.employeeId) {queryParams.set('employeeId', params.employeeId)}
      if (params.action) {queryParams.set('action', params.action)}
      if (params.module) {queryParams.set('module', params.module)}
      if (params.startDate) {queryParams.set('startDate', params.startDate)}
      if (params.endDate) {queryParams.set('endDate', params.endDate)}
      
      const queryString = queryParams.toString()
      const url = `/system/audit-logs/export${queryString ? `?${queryString}` : ''}`
      
      // 获取 CSV 数据
      const response = await get<string>(url)
      return response
    },
    onSuccess: (csvData) => {
      // 创建 Blob 并下载
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `audit-logs-${new Date().toISOString().split('T')[0]}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    },
  })
}

// ==================== Helper Constants ====================

export const AUDIT_ACTION_LABELS: Record<AuditAction, string> = {
  create: '创建',
  update: '更新',
  delete: '删除',
  approve: '审批',
  reject: '拒绝',
  login: '登录',
  logout: '登出',
}

export const AUDIT_MODULE_LABELS: Record<string, string> = {
  auth: '认证',
  employee: '员工',
  department: '部门',
  position: '职位',
  leave: '请假',
  salary: '薪资',
  team: '团队',
  project: '项目',
  issue: '任务',
  milestone: '里程碑',
  timelog: '工时',
  comment: '评论',
  account: '账户',
  category: '分类',
  cash_flow: '现金流',
  reimbursement: '报销',
  system_config: '系统配置',
  notification_preference: '通知偏好',
}
